/**
 * VYPER Cart Enhancements
 * – VyperGiftBar   : gift threshold logic + auto-add / auto-remove
 * – Upsell ATC     : AJAX add-to-cart for upsell strip & empty-cart grid
 * – Manual gift add: button inside gift reveal block
 */

// ── Section Rendering Refresh ─────────────────────────────────────────────────
// _internalRefresh: set while WE are replacing .side-panel-content so the
// MutationObserver knows to skip vyperSyncGiftBar (avoids double-trigger).
let _internalRefresh = false;

async function vyperRefreshCart() {
  try {
    const res = await fetch('/?sections=cart-drawer,cart-bubble');
    if (!res.ok) return;
    const data = await res.json();

    if (data['cart-drawer']) {
      const tmp = document.createElement('div');
      tmp.innerHTML = data['cart-drawer'];
      const curDrawer = document.getElementById('Cart-Drawer');

      // Update scrollable content — flag so observer skips gift-bar sync
      const newContent = tmp.querySelector('.side-panel-content');
      if (newContent && curDrawer) {
        const curContent = curDrawer.querySelector('.side-panel-content');
        if (curContent) {
          _internalRefresh = true;
          curContent.innerHTML = newContent.innerHTML;
          setTimeout(function () { _internalRefresh = false; }, 150);
        }
      }

      // Sync header count (outside .side-panel-content)
      const newCountEl = tmp.querySelector('.vyper-drawer__count');
      const curCountEl = curDrawer && curDrawer.querySelector('.vyper-drawer__count');
      if (newCountEl && curCountEl) curCountEl.textContent = newCountEl.textContent;

      // Sync gift bar data attrs from fresh server HTML
      const newGiftBar = tmp.querySelector('vyper-gift-bar');
      const curGiftBar = curDrawer && curDrawer.querySelector('vyper-gift-bar');
      if (newGiftBar && curGiftBar) {
        curGiftBar.dataset.inCart = newGiftBar.dataset.inCart;
        curGiftBar.dataset.total  = newGiftBar.dataset.total;
      }

      // Sync header bars (text + fill) — they live in <header>, outside
      // .side-panel-content, so they never update on their own.
      tmp.querySelectorAll('.vyper-header-bar').forEach(function (newBar, i) {
        const curBars = curDrawer
          ? curDrawer.querySelectorAll('.vyper-header-bar')
          : document.querySelectorAll('#Cart-Drawer .vyper-header-bar');
        const curBar = curBars && curBars[i];
        if (!curBar) return;
        const newText = newBar.querySelector('.vyper-header-bar__text');
        const curText = curBar.querySelector('.vyper-header-bar__text');
        if (newText && curText) curText.innerHTML = newText.innerHTML;
        const newFill = newBar.querySelector('.vyper-header-bar__fill');
        const curFill = curBar.querySelector('.vyper-header-bar__fill');
        if (newFill && curFill) {
          curFill.style.width = newFill.style.width;
          curFill.classList.toggle('is-full', newFill.classList.contains('is-full'));
        }
      });
    }

    if (data['cart-bubble']) {
      const tmp = document.createElement('div');
      tmp.innerHTML = data['cart-bubble'];
      const newCount = tmp.querySelector('.float_count');
      // Cart-Toggle ONLY — the wishlist badge ALSO uses .float_count class,
      // and document.querySelector returned that one first → cart's count
      // was overwriting the wishlist count.
      const curCount = document.querySelector('#cart-drawer-toggle .float_count');
      if (newCount && curCount) curCount.textContent = newCount.textContent;
    }
  } catch (e) {
    console.warn('[VYPER] Cart refresh error', e);
  }
}

// ── Sync gift bar after theme-initiated cart changes ──────────────────────────
// When the theme's cart-remove button fires (CartDrawer.updateQuantity), it
// replaces .side-panel-content but does NOT touch <header>. The gift bar's
// data-total stays stale → attributeChangedCallback never fires → auto-remove
// doesn't trigger. This function fixes that by fetching /cart.js and pushing
// fresh values to the gift bar element.
//
// Also de-duplicates extra gift items from previous test sessions.
let _giftSyncTimer = null;
function vyperSyncGiftBar() {
  clearTimeout(_giftSyncTimer);
  _giftSyncTimer = setTimeout(_doSyncGiftBar, 150);
}

async function _doSyncGiftBar() {
  // Find ALL gift bars on the page (drawer + cart page can both have one)
  const giftBars = document.querySelectorAll('vyper-gift-bar');
  if (!giftBars.length) return;
  const variantId = parseInt(giftBars[0].dataset.variantId, 10) || 0;

  try {
    const res = await fetch('/cart.js');
    if (!res.ok) return;
    const cart = await res.json();

    // If no gift variant configured, still sync data-total so the bar's
    // _render() updates text + fill correctly when cart contents change.
    if (!variantId) {
      giftBars.forEach(function (gb) {
        gb.dataset.total = String(cart.total_price);
      });
      return;
    }

    const giftItems = cart.items.filter(function (item) {
      return item.variant_id === variantId &&
             item.properties && item.properties._gift === 'true';
    });

    // De-duplicate: if more than one gift line item exists (from previous
    // test adds), remove the extras silently.
    if (giftItems.length > 1) {
      for (let i = 1; i < giftItems.length; i++) {
        await fetch('/cart/change.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ id: giftItems[i].key, quantity: 0 })
        });
      }
      await vyperRefreshCart();
      return; // refresh will re-run sync via attributeChangedCallback
    }

    // Compute non-gift total (so gift's own price doesn't count toward threshold)
    const nonGiftTotal = cart.items.reduce(function (sum, item) {
      if (item.variant_id === variantId &&
          item.properties && item.properties._gift === 'true') return sum;
      return sum + item.final_line_price;
    }, 0);

    // Push fresh values to ALL gift bars — triggers attributeChangedCallback → _check()
    giftBars.forEach(function (gb) {
      gb.dataset.total  = String(nonGiftTotal);
      gb.dataset.inCart = String(giftItems.length > 0);
    });
  } catch (e) {}
}

// ── Sync header count after any cart change ───────────────────────────────────
// The header is outside .side-panel-content so neither the theme's
// updateQuantity nor our vyperRefreshCart replaces it automatically.
// Strategy: debounced sync from /cart.js, triggered by:
//   (a) theme events: line-item:change:end, cart:item-added
//   (b) MutationObserver watching .side-panel-content as a catch-all

let _countSyncTimer = null;
async function vyperSyncCount() {
  clearTimeout(_countSyncTimer);
  _countSyncTimer = setTimeout(async function () {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();

      const countEl = document.querySelector('#Cart-Drawer .vyper-drawer__count');
      if (countEl) countEl.textContent = `(${cart.item_count})`;

      // Same .float_count collision as above — scope to cart toggle.
      const bubbleEl = document.querySelector('#cart-drawer-toggle .float_count');
      if (bubbleEl) bubbleEl.textContent = cart.item_count > 0 ? String(cart.item_count) : '0';
    } catch (e) {}
  }, 120);
}

// Theme + app events (dispatched on document / window)
// Includes cart-drawer open — covers removals from slider / outside drawer.
['line-item:change:end', 'cart:item-added', 'cart:update', 'cart:refresh',
 'cart-drawer:open', 'shopify:cart:update', 'vyper:cart:change'].forEach(function (evt) {
  document.addEventListener(evt, function () { vyperSyncCount(); vyperSyncGiftBar(); });
  window.addEventListener(evt,   function () { vyperSyncCount(); vyperSyncGiftBar(); });
});

// Hardened close-button handler for the cart drawer. The theme binds click
// at connectedCallback time only and on iOS Safari we've seen cases where the
// click never reaches that handler (touch event delivery, stale custom-element
// references after section refresh). Delegated capture-phase listener fires
// reliably regardless and idempotently closes the drawer via OverlayManager
// or as a fallback by setting the inert attribute the theme uses.
function vyperCloseCartDrawer() {
  const drawer = document.getElementById('Cart-Drawer');
  if (!drawer) return;
  if (window.OverlayManager && typeof OverlayManager.close === 'function') {
    OverlayManager.close(drawer);
  } else {
    drawer.setAttribute('inert', '');
    if (drawer.tl && typeof drawer.tl.timeScale === 'function') {
      drawer.tl.timeScale(2).reverse();
    }
    document.body.classList.remove('overlay-on', 'cart-drawer-open');
  }
}

['click', 'touchend'].forEach(function (evt) {
  document.addEventListener(evt, function (e) {
    const drawer = document.getElementById('Cart-Drawer');
    if (!drawer || drawer.hasAttribute('inert')) return;
    const closeBtn = e.target.closest('.thb-close');
    if (!closeBtn) return;
    // Only act on the drawer's own header close button — not the per-item
    // remove buttons that also render thb-close as their icon class.
    if (!drawer.contains(closeBtn)) return;
    if (closeBtn.closest('cart-remove')) return;
    e.preventDefault();
    vyperCloseCartDrawer();
  }, { capture: false, passive: false });
});

// MutationObserver: fires when .side-panel-content innerHTML is replaced.
// _internalRefresh guards against reacting to our OWN vyperRefreshCart updates.
document.addEventListener('DOMContentLoaded', function () {
  const sideContent = document.querySelector('#Cart-Drawer .side-panel-content');
  if (!sideContent) return;
  new MutationObserver(function () {
    vyperSyncCount();
    if (!_internalRefresh) {
      // Theme-initiated change (cart-remove, quantity update):
      // refresh cart to update header bars + gift bar data attrs.
      vyperRefreshCart().then(function () {
        vyperSyncGiftBar();
        setTimeout(vyperLoadRecommendations, 250);
      });
    }
  }).observe(sideContent, { childList: true });
});

// ── Dynamic Product Recommendations ───────────────────────────────────────────
// When the cart drawer opens, fetch Shopify's related-product recommendations
// for the first cart item and replace the static collection-based upsell list.
// Falls back gracefully to the collection list if the API returns nothing.
function vyperEsc(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

async function vyperLoadRecommendations() {
  const upsellEl = document.querySelector(
    '#Cart-Drawer .vyper-upsell:not(.vyper-upsell--empty)'
  );
  if (!upsellEl) return;

  const pid    = upsellEl.dataset.firstPid;
  const maxRaw = upsellEl.dataset.max;
  if (!pid || pid === '0') return;

  const cartPids = (upsellEl.dataset.cartPids || '')
    .split(',')
    .map(Number)
    .filter(Boolean);
  const max = parseInt(maxRaw, 10) || 2;

  try {
    const res = await fetch(
      `/recommendations/products.json?product_id=${pid}&limit=8&intent=related`
    );
    if (!res.ok) return;
    const data = await res.json();

    const products = (data.products || [])
      .filter(p => !cartPids.includes(p.id))
      .slice(0, max);

    if (!products.length) return; // keep collection list as-is

    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CHF';

    const html = products.map(function (prod) {
      const variant = prod.variants && prod.variants[0];
      if (!variant) return '';
      const price = new Intl.NumberFormat('de-CH', {
        style: 'currency', currency
      }).format(prod.price / 100);
      const imgSrc = prod.featured_image
        ? String(prod.featured_image).replace(
            /(\.(jpe?g|png|gif|webp))(\?|$)/i,
            '_100x100_crop_center$1$3'
          )
        : '';
      const title = vyperEsc(
        prod.title.length > 40 ? prod.title.slice(0, 40) + '…' : prod.title
      );
      return [
        '<li class="vyper-upsell__item">',
        `  <a href="/products/${vyperEsc(prod.handle)}" class="vyper-upsell__image" tabindex="-1">`,
        imgSrc
          ? `    <img src="${vyperEsc(imgSrc)}" alt="" width="44" height="44" loading="lazy">`
          : '',
        '  </a>',
        '  <div class="vyper-upsell__info">',
        `    <span class="vyper-upsell__name">${title}</span>`,
        `    <span class="vyper-upsell__price">${vyperEsc(price)}</span>`,
        '  </div>',
        `  <button class="vyper-upsell__btn" data-variant="${variant.id}"`,
        `          aria-label="${document.documentElement.lang === 'en' ? 'Add to cart' : 'In den Warenkorb'}: ${title}">+</button>`,
        '</li>'
      ].join('\n');
    }).join('');

    if (!html) return;
    const list = upsellEl.querySelector('.vyper-upsell__list');
    if (list) list.innerHTML = html;
  } catch (e) {
    // Fail silently — static collection list stays visible
  }
}

// Trigger on cart-drawer open and after our own refreshes
document.addEventListener('cart-drawer:open', vyperLoadRecommendations);
window.addEventListener('cart-drawer:open',   vyperLoadRecommendations);

// ── VyperGiftBar Custom Element ───────────────────────────────────────────────
if (!customElements.get('vyper-gift-bar')) {
  class VyperGiftBar extends HTMLElement {

    // Watch data-total and data-in-cart so we react to every cart change,
    // not just the initial page load.
    static get observedAttributes() {
      return ['data-total', 'data-in-cart'];
    }

    connectedCallback()                          { this._render(); this._check(); }
    attributeChangedCallback(_n, oldVal, newVal) {
      if (oldVal !== newVal) { this._render(); this._check(); }
    }

    // Self-render text + fill from current data-total / data-threshold so the
    // bar never shows stale "freigeschaltet" after items have been removed.
    _render() {
      const total     = parseInt(this.dataset.total,     10) || 0;
      const threshold = parseInt(this.dataset.threshold, 10) || 0;
      if (!threshold) return;
      const textEl = this.querySelector('.vyper-header-bar__text');
      const fillEl = this.querySelector('.vyper-header-bar__fill');
      const label  = this.dataset.label || 'Gratisprodukt';
      const pct    = Math.min(100, Math.round((total * 100) / threshold));
      if (textEl) {
        if (total >= threshold) {
          textEl.innerHTML = '<span class="vyper-header-bar__text--unlocked">✓ ' +
            label.replace(/[<>&"]/g, function(c){return {'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;'}[c];}) +
            ' freigeschaltet</span>';
        } else {
          const remainingCents = threshold - total;
          const chf  = Math.floor(remainingCents / 100);
          const rest = remainingCents % 100;
          const remStr = rest === 0
            ? 'CHF ' + chf + '.–'
            : 'CHF ' + chf + '.' + (rest < 10 ? '0' + rest : rest);
          textEl.innerHTML = (document.documentElement.lang === 'en' ? '<strong>' + remStr + '</strong> to ' : 'Noch <strong>' + remStr + '</strong> bis ') +
            label.replace(/[<>&"]/g, function(c){return {'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;'}[c];});
        }
      }
      if (fillEl) {
        fillEl.style.width = pct + '%';
        fillEl.classList.toggle('is-full', pct >= 100);
      }
    }

    _check() {
      if (this._busy) return;
      const total     = parseInt(this.dataset.total,     10) || 0;
      const threshold = parseInt(this.dataset.threshold, 10) || 0;
      const variantId = parseInt(this.dataset.variantId, 10) || 0;
      const autoAdd   = this.dataset.autoAdd === 'true';
      const inCart    = this.dataset.inCart  === 'true';

      if (!variantId || !threshold) return;

      // Only ONE bar may mutate the cart. When a cart-page (non-drawer) bar is
      // present it is the designated actor (it reloads the page so the cart
      // table refreshes); otherwise the drawer bar acts. Prevents the double
      // gift-add that occurs when the cart-page bar AND the drawer bar both
      // live on /cart and each fire connectedCallback -> _check().
      if (!this._isDesignatedActor()) return;

      // Auto-add: threshold reached, gift not yet in cart
      if (autoAdd && total >= threshold && !inCart) {
        if (window.__vyperGiftBusy) return;   // cross-bar lock
        window.__vyperGiftBusy = true;
        this._busy = true;
        this._addGift(variantId);
        return;
      }

      // Auto-remove: threshold no longer met but gift is still in cart
      if (total < threshold && inCart) {
        if (window.__vyperGiftBusy) return;
        window.__vyperGiftBusy = true;
        this._busy = true;
        this._removeGift(variantId);
      }
    }

    // The single bar allowed to auto add/remove the gift. If a cart-page
    // (non-drawer) bar exists it wins (its _addGift reloads -> table updates);
    // otherwise the drawer bar acts.
    _isDesignatedActor() {
      var pageBar = null;
      document.querySelectorAll('vyper-gift-bar').forEach(function (b) {
        if (!b.closest('#Cart-Drawer')) pageBar = b;
      });
      return pageBar ? (this === pageBar) : true;
    }

    // Returns true if this element lives inside the cart drawer.
    // Cart-page gift bars are outside the drawer and need a page reload
    // after any mutation so the table's data-index values stay valid.
    _inDrawer() {
      const drawer = document.getElementById('Cart-Drawer');
      return drawer ? drawer.contains(this) : false;
    }

    async _addGift(variantId) {
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1, properties: { _gift: 'true' } }]
          })
        });
        if (r.ok) {
          this.dataset.inCart = 'true'; // optimistic — prevents double-add
          if (this._inDrawer()) {
            await vyperRefreshCart();
          } else {
            window.location.reload();
            return;
          }
        }
      } catch (e) {
        console.warn('[VYPER] Gift add failed', e);
      } finally {
        this._busy = false;
        window.__vyperGiftBusy = false;
        this._check(); // re-check in case state changed while we were busy
      }
    }

    async _removeGift(variantId) {
      try {
        // Fetch cart to get the exact gift line-item key so we only remove
        // the _gift-flagged line item, not a regular item of the same variant.
        const cartRes = await fetch('/cart.js');
        if (!cartRes.ok) return;
        const cartData = await cartRes.json();

        const giftItem = cartData.items.find(function (item) {
          return item.variant_id === variantId &&
                 item.properties && item.properties._gift === 'true';
        });

        if (!giftItem) {
          // No gift line item found — update flag and bail
          this.dataset.inCart = 'false';
          if (this._inDrawer()) await vyperRefreshCart();
          else window.location.reload();
          return;
        }

        const r = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: giftItem.key, quantity: 0 })
        });
        if (r.ok) {
          this.dataset.inCart = 'false'; // optimistic
          if (this._inDrawer()) {
            await vyperRefreshCart();
          } else {
            window.location.reload();
            return;
          }
        }
      } catch (e) {
        console.warn('[VYPER] Gift remove failed', e);
      } finally {
        this._busy = false;
        window.__vyperGiftBusy = false;
        this._check(); // chain: removes one gift per cycle until all cleared
      }
    }
  }
  customElements.define('vyper-gift-bar', VyperGiftBar);
}

// ── AJAX Add-to-Cart (upsell strip + empty-cart grid) ────────────────────────
document.addEventListener('click', async function (e) {
  const btn = e.target.closest('.vyper-upsell__btn, .vyper-empty__atc');
  if (!btn) return;

  const variantId = parseInt(btn.dataset.variant, 10);
  if (!variantId) return;

  const orig = btn.textContent;
  btn.textContent = '…';
  btn.disabled = true;

  try {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });

    if (r.ok) {
      btn.textContent = '✓';
      await vyperRefreshCart();
      // Open drawer if it isn't already open
      const drawer = document.getElementById('Cart-Drawer');
      if (drawer && drawer.hasAttribute('inert')) {
        const toggle = document.getElementById('cart-drawer-toggle');
        if (toggle) toggle.click();
      }
      setTimeout(() => {
        btn.textContent = orig;
        btn.disabled    = false;
      }, 1800);
    } else {
      btn.textContent = orig;
      btn.disabled    = false;
    }
  } catch (e) {
    btn.textContent = orig;
    btn.disabled    = false;
  }
});

// ── Manual gift add (button inside gift reveal) ───────────────────────────────
document.addEventListener('click', async function (e) {
  const btn = e.target.closest('.vyper-gift-reveal__add');
  if (!btn) return;

  const variantId = parseInt(btn.dataset.variant, 10);
  if (!variantId) return;

  const orig = btn.textContent;
  btn.textContent = '…';
  btn.disabled = true;

  try {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { _gift: 'true' } }]
      })
    });
    if (r.ok) {
      await vyperRefreshCart();
    } else {
      btn.textContent = orig;
      btn.disabled    = false;
    }
  } catch (e) {
    btn.textContent = orig;
    btn.disabled    = false;
  }
});
